// Built-in faces (face templates spec section 6). Each presentation-1 shape is a face whose layers
// implement the v1 slots. A v1 template becomes a face extending `builtin:<shape>` with its slot
// bindings, drawn by the v1 card renderer so presentation-1 packages look exactly as before;
// presentation-2 faces may extend the same built-ins and draw them in SVG.

import type { Shape, Template } from '../presentation/types.ts';
import type { Box, FaceDef, Layer, Lod } from './types.ts';

/** Where each v1 slot draws on a built-in SVG face, in percent of the face. */
const CARD_SLOTS: Record<string, { box: Box; lod?: Lod[] }> = {
  band: { box: [0, 0, 100, 6] },
  title: { box: [6, 8, 76, 20] },
  corner: { box: [78, 7, 95, 19] },
  subtitle: { box: [6, 20, 94, 28], lod: ['thumb', 'full', 'zoom'] },
  art: { box: [6, 29, 94, 58], lod: ['thumb', 'full', 'zoom'] },
  text: { box: [6, 60, 94, 84], lod: ['full', 'zoom'] },
  cost: { box: [6, 85, 72, 95] },
  footer: { box: [6, 85, 78, 95] },
  badge: { box: [80, 84, 95, 96] },
};

const ROUND_SLOTS: Record<string, { box: Box; lod?: Lod[] }> = {
  fill: { box: [0, 0, 100, 100] },
  art: { box: [20, 12, 80, 60] },
  label: { box: [8, 30, 92, 70] },
  badge: { box: [66, 66, 98, 98] },
};

const EDGE_SLOTS: Record<string, { box: Box; lod?: Lod[] }> = {
  fill: { box: [0, 0, 100, 100] },
  label: { box: [10, 20, 90, 80] },
  badge: { box: [80, 0, 100, 40] },
};

const SLOTS: Record<Shape, Record<string, { box: Box; lod?: Lod[] }>> = {
  card: CARD_SLOTS, token: ROUND_SLOTS, tile: ROUND_SLOTS, space: ROUND_SLOTS, node: ROUND_SLOTS, edge: EDGE_SLOTS,
};

const SIZES: Record<Shape, { w: number; h: number; radius: number }> = {
  card: { w: 63, h: 88, radius: 4 },
  token: { w: 40, h: 40, radius: 20 },
  tile: { w: 60, h: 52, radius: 6 },
  space: { w: 60, h: 52, radius: 6 },
  node: { w: 40, h: 40, radius: 20 },
  edge: { w: 100, h: 20, radius: 4 },
};

/** Slot order is the drawing order. */
const ORDER = ['fill', 'band', 'art', 'title', 'label', 'corner', 'subtitle', 'text', 'cost', 'footer', 'badge'];

export const BUILTIN_SHAPES: Shape[] = ['card', 'token', 'tile', 'space', 'node', 'edge'];

/** `builtin:<shape>` with every v1 slot as an unbound layer (bindings come from the extending face). */
export function builtinFace(shape: Shape, bindings: Record<string, string> = {}): FaceDef {
  const slots = SLOTS[shape];
  const layers: Layer[] = ORDER.filter(s => s in slots).map(slot => ({
    id: slot, type: 'slot', slot, binding: bindings[slot] ?? '', box: slots[slot]!.box, ...(slots[slot]!.lod ? { lod: slots[slot]!.lod } : {}),
  }));
  return { id: `builtin:${shape}`, size: SIZES[shape], match: {}, layers };
}

export function isBuiltin(id: string): boolean {
  return id.startsWith('builtin:') && (BUILTIN_SHAPES as string[]).includes(id.slice(8));
}

/** A presentation-1 template as a face: the built-in shape with the template's slot bindings. */
export function templateFace(template: Template): FaceDef & { legacy: true; shape: Shape } {
  const base = builtinFace(template.shape, template.slots);
  return { ...base, id: `template:${template.id}`, match: template.match, legacy: true, shape: template.shape };
}
